/**
 * The only chrome allowed over a map canvas: a north arrow, a scale bar, the
 * attribution line and (3-D only) the vertical-exaggeration badge - together, in
 * the bottom-left corner, at one size on every page. Everything else a map needs
 * (legends, readouts, statistics, controls) lives in a panel beside it.
 */

import { useEffect, useState } from 'react'
import { useMap } from 'react-map-gl/maplibre'

import { C } from '../design/tokens'

export function niceScale(metresPerPixel: number, maxPx = 72): { px: number; label: string } {
  if (!(metresPerPixel > 0) || !Number.isFinite(metresPerPixel)) return { px: 0, label: '' }
  const target = metresPerPixel * maxPx
  const pow = Math.pow(10, Math.floor(Math.log10(target)))
  const step = [5, 2, 1].map((m) => m * pow).find((v) => v <= target) ?? pow
  return { px: step / metresPerPixel, label: step >= 1000 ? `${step / 1000} km` : `${step} m` }
}

export function CornerChrome({
  bearing,
  metresPerPixel,
  attribution,
  badge,
}: {
  bearing: number
  metresPerPixel: number | null
  attribution?: string
  badge?: string
}) {
  const scale = metresPerPixel ? niceScale(metresPerPixel) : null
  return (
    <div
      className="pointer-events-none absolute bottom-2 left-2 z-10 flex h-7 max-w-[calc(100%-16px)] items-center gap-2 rounded-[4px] px-2"
      style={{ background: C.chrome, fontSize: 'var(--fs-11)', lineHeight: 1, color: C.text }}
      data-testid="map-corner"
    >
      <svg width="14" height="14" viewBox="-7 -7 14 14" aria-label="North" style={{ transform: `rotate(${-bearing}deg)`, flex: 'none' }}>
        <path d="M0 -6 L4 5 L0 2.5 L-4 5 Z" fill={C.text} />
      </svg>
      {scale && scale.px > 0 && (
        <span className="flex flex-none items-center gap-1 tabular-nums" data-testid="scale-bar">
          <span style={{ width: scale.px, height: 5, borderLeft: `1px solid ${C.text}`, borderRight: `1px solid ${C.text}`, borderBottom: `1px solid ${C.text}` }} />
          {scale.label}
        </span>
      )}
      {badge && (
        <span className="flex-none font-semibold tabular-nums" data-testid="exaggeration-badge">
          {badge}
        </span>
      )}
      {attribution && (
        <span className="truncate" style={{ color: C.textDim }} title={attribution}>
          {attribution}
        </span>
      )}
    </div>
  )
}

/** The corner for a MapLibre map: follows the map's bearing and the scale at its centre. */
export function MapLibreCorner({ attribution }: { attribution?: string }) {
  const { current } = useMap()
  const [state, setState] = useState<{ bearing: number; mpp: number | null }>({ bearing: 0, mpp: null })
  useEffect(() => {
    const map = current?.getMap()
    if (!map) return undefined
    const update = () => {
      const c = map.getCenter()
      setState({ bearing: map.getBearing(), mpp: (40075016.686 * Math.cos((c.lat * Math.PI) / 180)) / (512 * 2 ** map.getZoom()) })
    }
    update()
    map.on('move', update)
    return () => {
      map.off('move', update)
    }
  }, [current])
  return <CornerChrome bearing={state.bearing} metresPerPixel={state.mpp} attribution={attribution} />
}

/** A legend that folds away: open by default on wide screens, closed below 1440 px. */
export function CollapsibleLegend({ title = 'Legend', children, testid }: { title?: string; children: React.ReactNode; testid?: string }) {
  const [open, setOpen] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 1440))
  return (
    <details open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)} data-testid={testid ?? 'map-legend'}>
      <summary className="section-label cursor-pointer select-none">{title}</summary>
      <div className="mt-1.5">{children}</div>
    </details>
  )
}
